import { supabase } from '../supabase';

const STORAGE_BUCKET = 'aziende';

/**
 * Uploaduje dokument partnera u Supabase Storage i upisuje kompletan red
 * (uključujući SVA metapodatka polja) u tabelu 'documenti_partnera' u JEDNOM
 * insert pozivu — za razliku od uploadCompanyDocument (koji ne prima mese/refid
 * i zahtijeva dodatni UPDATE poziv nakon uploada), ovaj servis odmah prima
 * i upisuje sva polja iz forme, čime se izbjegava potreba za follow-up update-om.
 *
 * Putanja u Storage-u prati strukturu:
 * partneri/{idAzienda}/{idPartnera}/{timestamp}_{fileName}
 *
 * @param {Object} params
 * @param {File} params.file - Fajl za upload
 * @param {string} params.idAzienda - ID matične kompanije (vlasnik partnera)
 * @param {string} params.idPartnera - ID partnera kojem dokument pripada
 * @param {string} params.uploadedBy - ID korisnika koji vrši upload
 * @param {string} params.tipologia - Tip/kategorija dokumenta
 * @param {number|string} params.anno - Godina dokumenta
 * @param {string} [params.mese] - Mjesec dokumenta ('01'..'12')
 * @param {string} [params.refid] - Referentni broj / broj fakture
 * @param {string} [params.validoDal] - Datum početka važenja
 * @param {string} [params.validoAl] - Datum kraja važenja
 * @param {string} [params.note] - Napomena
 */
export const uploadPartnerDocument = async ({
  file,
  idAzienda,
  idPartnera,
  uploadedBy,
  tipologia,
  anno,
  mese = null,
  refid = null,
  validoDal = null,
  validoAl = null,
  note = null
}) => {
  let filePath = null;

  try {
    if (!file) throw new Error('Fajl je obavezan za upload.');
    if (!idAzienda) throw new Error('ID kompanije je obavezan.');
    if (!idPartnera) throw new Error('ID partnera je obavezan.');

    // 1. Sanitizacija imena fajla i izgradnja putanje po traženoj strukturi
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    filePath = `partneri/${idAzienda}/${idPartnera}/${Date.now()}_${cleanFileName}`;

    // 2. Upload u Supabase Storage (bucket 'aziende')
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) throw uploadError;

    // 3. Upis KOMPLETNOG reda (svi metapodaci odjednom) u 'documenti_partnera'
    const { data: docData, error: dbError } = await supabase
      .from('documenti_partnera')
      .insert([{
        id_azienda: idAzienda,
        id_partnera: idPartnera,
        uploaded_by: uploadedBy,
        nome_file: file.name,
        file_path: filePath,
        tipologia,
        anno: parseInt(anno, 10) || new Date().getFullYear(),
        mese: mese || null,
        refid: refid || null,
        valido_dal: validoDal || null,
        valido_al: validoAl || null,
        note: note || null,
        is_deleted: false
      }])
      .select()
      .single();

    if (dbError) {
      // Rollback: ako upis u bazu padne, ukloni upravo uploadovani fajl
      // da ne ostane "orphan" objekat u Storage-u bez pripadajućeg reda.
      await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);
      throw dbError;
    }

    return { success: true, data: docData };
  } catch (err) {
    console.error('Greška pri uploadu dokumenta partnera:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Dohvata kategorije dokumenata koje pripadaju TAČNO ODREĐENOM partneru.
 * IZOLACIJA: kategorije nisu više zajedničke na nivou firme — svaki partner
 * ima svoju odvojenu listu, filtrirano i po id_azienda (vlasnička firma,
 * sigurnosna mjera) i po id_partnera (izolacija po partneru).
 * @param {string} id_azienda - ID matične kompanije
 * @param {string} id_partnera - ID partnera čije kategorije se dohvataju
 */
export const getKategorijeDokumentaPartnera = async (id_azienda, id_partnera) => {
  try {
    if (!id_azienda || !id_partnera) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('kategorije_dokumenta_partnera')
      .select('id, naziv, id_azienda, id_partnera')
      .eq('id_azienda', id_azienda)
      .eq('id_partnera', id_partnera)
      .order('naziv', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Greška pri dohvaćanju kategorija dokumenata partnera:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Kreira novu kategoriju dokumenata vezanu za TAČNO ODREĐENOG partnera.
 * Provjera duplikata naziva se radi SAMO unutar kategorija tog partnera —
 * partner A i partner B mogu imati kategoriju istog naziva bez sudara.
 * @param {string} naziv - Naziv kategorije
 * @param {string} id_azienda - ID matične kompanije
 * @param {string} id_partnera - ID partnera kojem kategorija pripada
 */
export const kreirajKategorijuDokumentaPartnera = async (naziv, id_azienda, id_partnera) => {
  try {
    if (!id_azienda) throw new Error('ID kompanije je obavezan za kreiranje kategorije.');
    if (!id_partnera) throw new Error('ID partnera je obavezan za kreiranje kategorije.');
    if (!naziv || !naziv.trim()) throw new Error('Naziv kategorije je obavezan.');

    const trimmedNaziv = naziv.trim();

    // Provjera duplikata: SAMO unutar kategorija ovog konkretnog partnera
    const { data: existing, error: checkError } = await supabase
      .from('kategorije_dokumenta_partnera')
      .select('id')
      .eq('id_partnera', id_partnera)
      .ilike('naziv', trimmedNaziv)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existing) {
      throw new Error('Kategorija sa ovim nazivom već postoji za ovog partnera.');
    }

    const { data, error } = await supabase
      .from('kategorije_dokumenta_partnera')
      .insert([{
        naziv: trimmedNaziv,
        id_azienda,
        id_partnera
      }])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Greška pri kreiranju kategorije dokumenata partnera:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Briše kategoriju dokumenata partnera, po id kategorije, id_azienda I
 * id_partnera (trostruki uslov osigurava da se ne može obrisati kategorija
 * koja pripada drugom partneru ili drugoj firmi, čak i ako se pogodi ID).
 * @param {string} katId - ID kategorije
 * @param {string} id_azienda - ID matične kompanije
 * @param {string} id_partnera - ID partnera kojem kategorija pripada
 */
export const obrisiKategorijuDokumentaPartnera = async (katId, id_azienda, id_partnera) => {
  try {
    if (!katId || !id_azienda || !id_partnera) {
      throw new Error('ID kategorije, ID kompanije i ID partnera su obavezni.');
    }

    const { error } = await supabase
      .from('kategorije_dokumenta_partnera')
      .delete()
      .eq('id', katId)
      .eq('id_azienda', id_azienda)
      .eq('id_partnera', id_partnera);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Greška pri brisanju kategorije dokumenata partnera:', error);
    return { success: false, error: error.message };
  }
};